import React, { useState, useEffect, useContext } from "react";
import {
  Alert,
  Box,
  Card,
  CardContent,
  Container,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  Grid,
  CircularProgress,
  Button,
  Paper,
  Divider,
  Snackbar,
} from "@mui/material";
import { apiRequest } from "../services/apiService";
import { UserDataContext } from "../components/UserDataContext";

export default function CitySelector() {
  const { userData } = useContext(UserDataContext);
  const [voivodeshipList, setVoivodeshipList] = useState([]);
  const [selectedVoivodeship, setSelectedVoivodeship] = useState("");
  const [cities, setCities] = useState([]);
  const [selectedCities, setSelectedCities] = useState([]);
  const [loadingCities, setLoadingCities] = useState(false);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    const initData = async () => {
      try {
        const vResponse = await apiRequest("voivodeship");
        setVoivodeshipList(vResponse || []);

        if (userData?.userId) {
          const savedResponse = await apiRequest(
            `get-user-cities/${userData.userId}`,
            "GET",
          );

          if (savedResponse.success && Array.isArray(savedResponse.cities)) {
            const extractedCityIds = savedResponse.cities.map((item) =>
              Number(item.cityId),
            );
            setSelectedCities(extractedCityIds);
            if (savedResponse.cities.length > 0) {
              const firstVoivodeId = Number(
                savedResponse.cities[0].voivodeshipId,
              );
              setSelectedVoivodeship(firstVoivodeId);
            }
          }
        }
      } catch (error) {
        console.error("Błąd inicjalizacji:", error);
      }
    };

    initData();
  }, [userData?.userId]);

  useEffect(() => {
    if (!selectedVoivodeship) {
      setCities([]);
      return;
    }

    const fetchCities = async () => {
      setLoadingCities(true);

      try {
        const response = await apiRequest(`cities/${selectedVoivodeship}`);
        setCities(Array.isArray(response) ? response : []);
      } catch (error) {
        console.error("Błąd pobierania miast:", error);
        setCities([]);
      } finally {
        setLoadingCities(false);
      }
    };

    fetchCities();
  }, [selectedVoivodeship]);

  const handleToggleCity = (cityId) => {
    setSelectedCities((prev) =>
      prev.includes(cityId)
        ? prev.filter((id) => id !== cityId)
        : [...prev, cityId],
    );
  };

  const handleSave = async () => {
    const payload = {
      voivodeshipId: selectedVoivodeship,
      cityIds: selectedCities,
    };

    await apiRequest("save-user-cities", "POST", payload);
    setSaved(true);
  };

  return (
    <Container>
      <Card>
        <CardContent>
          <Typography variant="h5" mb={2}>
            Gdzie mogę pracować
          </Typography>

          <Alert severity="info" sx={{ mb: 3 }}>
            Wybierz województwo oraz miasta, w których możesz podjąć pracę.
          </Alert>

          <FormControl fullWidth>
            <InputLabel>Województwo</InputLabel>

            <Select
              value={selectedVoivodeship}
              label="Województwo"
              onChange={(e) => setSelectedVoivodeship(e.target.value)}
            >
              {voivodeshipList.map((item) => (
                <MenuItem key={item.id} value={item.id}>
                  {item.nazwa}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          {selectedVoivodeship && (
            <>
              <Divider sx={{ my: 3 }} />

              <Typography variant="subtitle1" mb={2}>
                Wybierz miasta
              </Typography>

              <Box
                sx={{
                  border: "1px solid",
                  borderColor: "divider",
                  borderRadius: 2,
                  maxHeight: 320,
                  overflowY: "auto",
                  p: 2,
                }}
              >
                {loadingCities ? (
                  <Box
                    sx={{
                      display: "flex",
                      justifyContent: "center",
                      py: 4,
                    }}
                  >
                    <CircularProgress />
                  </Box>
                ) : (
                  <Grid container spacing={1}>
                    {cities.map((city) => (
                      <Grid item xs={12} sm={6} md={4} key={city.id}>
                        <FormControlLabel
                          sx={{
                            width: "100%",
                            borderRadius: 1,
                            "&:hover": {
                              backgroundColor: "action.hover",
                            },
                          }}
                          control={
                            <Checkbox
                              checked={selectedCities.includes(Number(city.id))}
                              onChange={() => handleToggleCity(Number(city.id))}
                            />
                          }
                          label={city.nazwa}
                        />
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            </>
          )}

          <Button
            variant="contained"
            size="large"
            fullWidth
            sx={{ mt: 3 }}
            disabled={selectedCities.length === 0}
            onClick={handleSave}
          >
            Zapisz wybrane miasta ({selectedCities.length})
          </Button>

          <Snackbar
            open={saved}
            autoHideDuration={3000}
            onClose={() => setSaved(false)}
          >
            <Alert severity="success" variant="filled">
              Zapisano miasta
            </Alert>
          </Snackbar>
        </CardContent>
      </Card>
    </Container>
  );
}
